import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { GOOGLE_CLIENT_ID, GOOGLE_SSO_BYPASS } from "../lib/config";
import { AppError, logError, toUserMessage } from "../lib/errors";
import { api } from "../lib/api";
import { LINE_AUTH_MODE_KEY, markWebAppLineRegisterEntry } from "../lib/lineRegisterSource";
import { clearFrontendAuthState } from "../lib/logout";
import { storage } from "../lib/storage";
import { useSsoFlow, type SsoLoginResponse } from "../hooks/useSsoFlow";
import { bootstrapAuth, type BootstrapPhase } from "../services/auth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (element: HTMLElement, options: object) => void;
        };
      };
    };
  }
}

type Phase =
  | "initializing"
  | "googleSignIn"
  | "googleVerifying"
  | BootstrapPhase
  | "navigating"
  | "error"
  | "forbidden";

const GOOGLE_GSI_SCRIPT_SELECTOR = 'script[data-google-gsi-script="true"]';
const NON_PERSONALIZED_GOOGLE_BUTTON_WIDTH_PX = "196";

let googleGsiLoader: Promise<void> | null = null;

function getGoogleAccountsId() {
  return window.google?.accounts?.id ?? null;
}

function hasStoredAuthToken() {
  const { accessToken, refreshToken } = storage.getAuth();
  return Boolean(accessToken || refreshToken);
}

function shouldRecoverToGoogleSignIn(errorValue: unknown, verifyStoredAuth?: boolean) {
  if (!(errorValue instanceof AppError)) return false;
  if (errorValue.code === "SESSION_EXPIRED") return true;
  if (!verifyStoredAuth) return false;
  return (
    errorValue.code === "UNAUTHORIZED" ||
    errorValue.code === "FORBIDDEN" ||
    errorValue.code === "NOT_FOUND"
  );
}

function loadGoogleIdentityServices(): Promise<void> {
  if (getGoogleAccountsId()) {
    return Promise.resolve();
  }

  if (!googleGsiLoader) {
    googleGsiLoader = new Promise((resolve, reject) => {
      let script = document.querySelector<HTMLScriptElement>(GOOGLE_GSI_SCRIPT_SELECTOR);

      if (script?.dataset.loaded === "error") {
        script.remove();
        script = null;
      }

      const targetScript = script ?? document.createElement("script");

      const cleanup = () => {
        targetScript.removeEventListener("load", handleLoad);
        targetScript.removeEventListener("error", handleError);
      };

      const fail = (message: string) => {
        cleanup();
        googleGsiLoader = null;
        reject(new Error(message));
      };

      const handleLoad = () => {
        targetScript.dataset.loaded = "true";
        cleanup();
        if (getGoogleAccountsId()) {
          resolve();
          return;
        }
        googleGsiLoader = null;
        reject(new Error("Google Identity Services did not initialize."));
      };

      const handleError = () => {
        targetScript.dataset.loaded = "error";
        fail("Failed to load Google Identity Services.");
      };

      if (getGoogleAccountsId()) {
        resolve();
        return;
      }

      if (targetScript.dataset.loaded === "true") {
        fail("Google Identity Services did not initialize.");
        return;
      }

      targetScript.addEventListener("load", handleLoad);
      targetScript.addEventListener("error", handleError);

      if (!script) {
        targetScript.src = "https://accounts.google.com/gsi/client";
        targetScript.async = true;
        targetScript.defer = true;
        targetScript.dataset.googleGsiScript = "true";
        targetScript.dataset.loaded = "loading";
        document.head.appendChild(targetScript);
      }
    });
  }

  return googleGsiLoader;
}


export default function Splash() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>("initializing");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const runningRef = useRef(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  const runBootstrap = useCallback(async (navigateTo = "/home", opts?: { verifyStoredAuth?: boolean }) => {
    try {
      setErrorMessage(null);
      await bootstrapAuth({
        onPhase: (currentPhase: BootstrapPhase) => setPhase(currentPhase),
      });
      if (opts?.verifyStoredAuth) {
        await api.getUser();
      }
      setPhase("navigating");
      navigate(navigateTo, { replace: true });
    } catch (errorValue) {
      if (shouldRecoverToGoogleSignIn(errorValue, opts?.verifyStoredAuth)) {
        clearFrontendAuthState();
        setPhase("googleSignIn");
        runningRef.current = false;
        return;
      }
      logError(errorValue);
      setPhase("error");
      setErrorMessage(toUserMessage(errorValue));
      runningRef.current = false;
    }
  }, [navigate]);

  const handleSsoSuccess = useCallback(
    async (ssoResponse: SsoLoginResponse) => {
      const { userId, secretKey, accessToken, refreshToken } = ssoResponse;

      if (userId && accessToken) {
        const currentAuth = storage.getAuth();
        const isSameUser = currentAuth.userId === userId;
        const effectiveSecretKey = secretKey ?? (isSameUser ? currentAuth.secretKey : undefined);

        storage.setAuth({
          userId,
          secretKey: effectiveSecretKey,
          accessToken,
          refreshToken,
          authProvider: "google",
          currentCharacterId: isSameUser ? currentAuth.currentCharacterId : undefined,
          currentVoiceId: isSameUser ? currentAuth.currentVoiceId : undefined,
          terminalIdentificationNumber: currentAuth.terminalIdentificationNumber,
        });
      }

      await runBootstrap(ssoResponse.isNewUser === true ? "/onboarding" : "/home");
    },
    [runBootstrap]
  );

  const { startSsoLogin } = useSsoFlow(handleSsoSuccess);

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      if (runningRef.current) return;
      runningRef.current = true;

      setPhase("googleVerifying");
      setErrorMessage(null);

      try {
        await startSsoLogin(credential, 'login');
      } catch (errorValue) {
        if (errorValue instanceof AppError && errorValue.code === "FORBIDDEN") {
          setPhase("googleSignIn");
          setErrorMessage("このアカウントではログインできません。");
        } else if (errorValue instanceof AppError && errorValue.code === "NOT_FOUND") {
          setPhase("googleSignIn");
          setErrorMessage("この方法での登録が見つかりません。下の「新規登録」から登録してください。");
        } else {
          logError(errorValue);
          setPhase("googleSignIn");
          setErrorMessage(toUserMessage(errorValue));
        }
        runningRef.current = false;
      }
    },
    [startSsoLogin]
  );

  const handleStartLineRegister = useCallback(() => {
    setErrorMessage(null);
    sessionStorage.setItem(LINE_AUTH_MODE_KEY, 'login');
    markWebAppLineRegisterEntry();
    navigate("/line-login");
  }, [navigate]);

  useEffect(() => {
    if (GOOGLE_SSO_BYPASS) {
      runningRef.current = true;
      void runBootstrap();
      return;
    }

    const auth = storage.getAuth();
    if (auth.accessToken || auth.refreshToken) {
      runningRef.current = true;
      void runBootstrap("/home", { verifyStoredAuth: true });
      return;
    }

    if (!GOOGLE_CLIENT_ID) {
      setPhase("googleSignIn");
      setErrorMessage("Google Client ID が設定されていません。");
      return;
    }

    setPhase("googleSignIn");
  }, [runBootstrap]);

  useEffect(() => {
    if (phase !== "googleSignIn" || !GOOGLE_CLIENT_ID || hasStoredAuthToken()) {
      return;
    }

    let disposed = false;

    const renderGoogleButton = async () => {
      try {
        await loadGoogleIdentityServices();

        if (disposed || hasStoredAuthToken()) return;

        const googleAccountsId = getGoogleAccountsId();
        const buttonContainer = googleBtnRef.current;
        if (!googleAccountsId || !buttonContainer) return;

        buttonContainer.innerHTML = "";
        googleAccountsId.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: ({ credential }) => {
            void handleGoogleCredential(credential);
          },
        });
        googleAccountsId.renderButton(buttonContainer, {
          theme: "outline",
          size: "large",
          width: NON_PERSONALIZED_GOOGLE_BUTTON_WIDTH_PX,
          locale: "ja",
        });
      } catch (errorValue) {
        if (disposed) return;
        logError(errorValue);
        setPhase("googleSignIn");
        setErrorMessage(
          "Google でログインする準備に失敗しました。ページを読み直して、もう一度お試しください。"
        );
        runningRef.current = false;
      }
    };

    void renderGoogleButton();

    return () => {
      disposed = true;
    };
  }, [phase, handleGoogleCredential]);

  const handleRetry = () => {
    if (phase !== "error") return;
    runningRef.current = true;
    void runBootstrap();
  };

  const phaseLabel =
    phase === "googleVerifying"
      ? "認証を確認しています..."
      : phase === "creatingUser"
        ? "ユーザーを準備しています..."
        : phase === "loggingIn"
          ? "ログインしています..."
          : phase === "eventLogin"
            ? "ログイン情報を反映しています..."
            : phase === "checking"
              ? "確認しています..."
              : phase === "navigating"
                ? "移動しています..."
                : "";

  const isLoading =
    phase === "initializing" ||
    phase === "googleVerifying" ||
    phase === "checking" ||
    phase === "creatingUser" ||
    phase === "loggingIn" ||
    phase === "eventLogin" ||
    phase === "navigating";

  return (
    <main className="flex min-h-screen select-none items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
      <div className="w-full max-w-sm">
        <div className="relative overflow-hidden rounded-[2.2rem] border border-[#EADBC8] bg-white/70 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-[#F4E3D7] opacity-70 blur-3xl" />

          <div className="px-8 pb-10 pt-12 text-center">
            <p className="text-[11px] font-bold tracking-[0.12em] text-[#A67C52]">PERSONAL CONCIERGE</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>

            <div className="mx-auto mb-6 mt-6 h-40 w-40 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)]">
              <img src="/img/logo.jpg" alt="WizMate Logo" className="h-full w-full object-cover object-center" />
            </div>

            <p className="text-sm font-medium text-[#7B6861]">あなた専属コンシェルジュを起動します</p>

            {phase === "googleSignIn" ? (
              <div className="mt-10">
                {errorMessage ? (
                  <p className="mb-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-xs font-medium text-[#A1493E]">
                    {errorMessage}
                  </p>
                ) : null}
                <section
                  aria-label="既存アカウントでログイン"
                  className="rounded-[1.4rem] border border-[#EADBC8] bg-white/60 px-4 py-5"
                >
                  <p className="mb-4 text-sm font-black text-[#5D4D46]">既存アカウントでログイン</p>
                  <div className="flex justify-center">
                    <div ref={googleBtnRef} />
                  </div>
                  <button
                    type="button"
                    className="mx-auto mt-3 flex h-10 w-[196px] items-center justify-center gap-2 rounded border border-[#06C755] bg-[#06C755] px-3 text-sm font-bold text-white shadow-[0_4px_12px_rgba(6,199,85,0.24)] transition hover:bg-[#05b34d] active:scale-[0.99]"
                    onClick={handleStartLineRegister}
                    aria-label="LINEでログイン"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded bg-white text-[10px] font-black leading-none text-[#06C755]">
                      LINE
                    </span>
                    <span>LINEでログイン</span>
                  </button>
                </section>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <p className="text-xs font-black text-[#A67C52]">または</p>
                  <button
                    type="button"
                    className="rounded-xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5] active:scale-[0.99]"
                    onClick={() => navigate("/email-otp/email?mode=login")}
                  >
                    メールで続ける
                  </button>
                </div>
                <p className="mt-4 text-xs text-[#9C867D]">
                  アカウントをお持ちでない方は{" "}
                  <Link to="/register" className="font-bold text-[#A67C52] underline underline-offset-2">新規登録</Link>
                </p>
              </div>
            ) : null}

            {isLoading ? (
              <div className="mt-10 rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4">
                <p className="text-sm font-bold text-[#5D4D46]">{phaseLabel}</p>
                <div className="mt-4 flex items-center justify-center gap-1.5" aria-label="loading">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.1s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F]" />
                </div>
              </div>
            ) : null}

            {phase === "forbidden" ? (
              <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                <p className="text-sm font-bold text-[#A1493E]">このアカウントでは利用できません</p>
                <p className="mt-1 text-xs text-[#A1493E]">{errorMessage}</p>
                <p className="mt-3 text-xs font-medium text-[#6B5851]">
                  アクセス条件については運営までお問い合わせください。
                </p>
              </div>
            ) : null}

            {phase === "error" ? (
              <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                <p className="text-sm font-bold text-[#A1493E]">通信に失敗しました</p>
                <p className="mt-1 break-words text-xs text-[#A1493E]">{errorMessage}</p>
                <button
                  className="mt-3 w-full rounded-xl border border-[#EADBC8] bg-white px-4 py-2 text-sm font-bold text-[#5D4D46] hover:bg-[#FDF8F5] active:scale-[0.99]"
                  onClick={handleRetry}
                >
                  再試行
                </button>
              </div>
            ) : null}
          </div>

          <div className="border-t border-[#EADBC8] px-6 py-4 text-center">
            <Link to="/support-inquiry" className="text-xs font-bold text-[#A67C52] underline underline-offset-2">
              ログインでお困りの方はこちら
            </Link>
            <p className="mt-2 text-[11px] text-[#9C867D]">© WizMate</p>
          </div>
        </div>
      </div>
    </main>
  );
}
